import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { fetchWorkersByProject, fetchMembersByProject, releaseWorker } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function BuildProjectMemberDetailPage() {
  const { businessDomainId, businessUnitId, projectId, memberId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const memberListPath = `/build/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/members`;

  const loadData = useCallback(async () => {
    try {
      const [workers, members] = await Promise.all([
        fetchWorkersByProject(projectId).catch(() => []),
        fetchMembersByProject(projectId).catch(() => []),
      ]);
      const found = (Array.isArray(workers) ? workers : []).find(
        (w) => String(w.memberId) === String(memberId)
      );
      if (found) {
        const memberInfo = (Array.isArray(members) ? members : []).find(
          (m) => String(m.memberId) === String(memberId)
        );
        setData({ ...found, industryId: memberInfo?.industryId });
      } else {
        setData(null);
      }
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRelease = async () => {
    try {
      const res = await releaseWorker(data.workerId);
      if (res?.result !== "OK") {
        throw new Error(res?.errorMessage || "release_failed");
      }
      notify.success(t("message.release.success"));
      navigate(memberListPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  const memberTypeLabel = data.industryId === "9999" ? t("member.type.company") : t("member.type.market");

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("project.member.detail.title")}</Typography>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("member.name")} value={data.memberName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("project.member.worker.name")} value={data.workerName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.type")} value={memberTypeLabel} />
          </Grid>
          {data.departmentName && (
            <Grid size={12} md={6}>
              <DetailField label={t("department.name")} value={data.departmentName} />
            </Grid>
          )}
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<ZoomInIcon />}
          onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goal-achievements`)}
        >
          {t("project.goalAchievement.list.title")}
        </Button>
        <Button variant="contained" color="error" onClick={() => setConfirmOpen(true)}>
          {t("member.release")}
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/members`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleRelease}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
